import React from 'react'
import {Tooltip} from 'react-tooltip'
import moment from 'moment'
import {KTIcon} from '../../../_metronic/helpers'

export const formatDashboardCurrency = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '-'
  const numericValue = Number(value)
  if (Number.isNaN(numericValue)) return '-'
  return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(numericValue)
}

export const formatDashboardRating = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '-'
  const numericValue = Number(value)
  return Number.isNaN(numericValue) ? '-' : numericValue.toFixed(2)
}

// export const formatDashboardDateTime = (value: string | null | undefined): string => {
//   if (!value) return '-'
//   const parsed = moment(value)
//   return parsed.isValid() ? parsed.format('MM/DD/YYYY hh:mm:ss A') : '-'
// }

export const formatDashboardDateTime = (
  value: string | null | undefined
): string => {
  if (!value) return '-'
  const parsed = moment(value)
  if (!parsed.isValid()) return '-'
  const fullMonths = [
    "Jan",
    "Feb",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ]
  const month = fullMonths[parsed.month()]
  return `${parsed.format("DD")} ${month} ${parsed.format("YYYY, hh:mm A")}`
}

let dashboardTooltipCounter = 0

const DashboardTruncatedCell: React.FC<{
  text: string
  href?: string
}> = ({text, href}) => {
  const [tooltipId] = React.useState(
    () => `dashboard-truncated-${++dashboardTooltipCounter}`
  )
  const textRef = React.useRef<HTMLDivElement>(null)
  const [isTruncated, setIsTruncated] = React.useState(false)

  React.useEffect(() => {
    const el = textRef.current
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth)
    }
  })

  const textContent = href ? (
    <a href={href} target='_blank' rel='noopener noreferrer'>
      {text}
    </a>
  ) : (
    text
  )

  return (
    <span
      className='d-flex'
      style={{minWidth: 0, width: '100%', overflow: 'hidden'}}
      data-tooltip-id={tooltipId}
      data-tooltip-content={text}
    >
      {isTruncated && (
        <Tooltip
          id={tooltipId}
          style={{maxWidth: '400px', wordWrap: 'break-word', whiteSpace: 'normal'}}
        />
      )}
      <div ref={textRef} className='display__dosts' style={{minWidth: 0, width: '100%'}}>
        {textContent}
      </div>
      {isTruncated && <span className='ellipsis'>...</span>}
    </span>
  )
}

export const renderDashboardLink = (url: string | null | undefined): React.ReactNode => {
  if (!url) return '-'
  return <DashboardTruncatedCell text={url} href={url} />
}

export const renderDashboardErrorMessage = (
  message: string | null | undefined
): React.ReactNode => {
  if (!message) return '-'
  return <DashboardTruncatedCell text={message} />
}

export const renderDashboardTruncatedText = (
  value: string | null | undefined
): React.ReactNode => {
  if (!value) return '-'
  return <DashboardTruncatedCell text={value} />
}

export const formatDashboardDate = (value: string | null | undefined): string => {
  if (!value) return '-'
  const parsed = moment.utc(value)
  return parsed.isValid() ? parsed.format('DD-MM-YYYY') : '-'
}

let dashboardLinkIconCounter = 0

const DashboardLinkIconCell: React.FC<{url: string}> = ({url}) => {
  const [tooltipId] = React.useState(() => `dashboard-link-icon-${++dashboardLinkIconCounter}`)
  return (
    <>
      <a
        href={url}
        target='_blank'
        rel='noopener noreferrer'
        className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm text-gray-600'
        data-tooltip-id={tooltipId}
        data-tooltip-content={url}
      >
        <svg
          width='18'
          height='18'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
          aria-hidden='true'
        >
          <path d='M15 3h6v6' />
          <path d='M10 14 21 3' />
          <path d='M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6' />
        </svg>
      </a>
      <Tooltip id={tooltipId} style={{maxWidth: 600, wordWrap: 'break-word', whiteSpace: 'normal'}} />
    </>
  )
}

export const renderDashboardLinkIcon = (url: string | null | undefined): React.ReactNode => {
  if (!url) return '-'
  return <DashboardLinkIconCell url={url} />
}

// Booleans (or "true"/"false" strings) render as TRUE / FALSE; anything else falls back to '-'.
export const formatDashboardBoolean = (value: string | boolean | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '-'
  const normalized = String(value).toLowerCase()
  if (normalized === 'true') return 'TRUE'
  if (normalized === 'false') return 'FALSE'
  return String(value)
}

export const renderDashboardActiveStatus = (
  value: string | boolean | null | undefined
): React.ReactNode => {
  const label = formatDashboardBoolean(value)
  if (label === '-') return label
  return <span className={`fw-semibold ${label === 'TRUE' ? 'text-success' : 'text-danger'}`}>{label}</span>
}

export const renderDashboardSku = (sku: string | null | undefined): React.ReactNode => (
  <span className='font-monospace fw-semibold'>{sku || '-'}</span>
)

// Seller name on the first line, seller id smaller and greyer below it.
export const renderDashboardSellerNameWithId = (
  name: string | null | undefined,
  id: string | number | null | undefined
): React.ReactNode => {
  if (!name && (id === null || id === undefined || id === '')) return '-'
  return (
    <div className='lh-sm'>
      <div className='fw-semibold'>{name || id}</div>
      {name && id && <div className='text-gray-600 fs-8'>{id}</div>}
    </div>
  )
}

// Date on the first line, time smaller and greyer below it.
export const renderDashboardDateTwoLine = (value: string | null | undefined): React.ReactNode => {
  const [datePart, timePart] = formatDashboardDateTime(value).split(', ')
  return (
    <div className='lh-sm'>
      <div className='fw-semibold'>{datePart}</div>
      {timePart && <div className='text-gray-600 fs-8'>{timePart}</div>}
    </div>
  )
}

// Pill with a status dot: "true" -> green TRUE, "false" -> red FALSE, anything else -> neutral uppercase text.
export const renderDashboardStatusPill = (
  value: string | boolean | null | undefined
): React.ReactNode => {
  if (value === null || value === undefined || value === '') return '-'
  const normalized = String(value).toLowerCase()
  const tone =
    normalized === 'true' || normalized === 'active'
      ? 'success'
      : normalized === 'false' || normalized === 'inactive' || normalized === 'deactivate'
      ? 'danger'
      : normalized === 'gray' || normalized === 'not listed' || normalized === 'not_listed'
      ? 'secondary'
      : 'primary'
  const label = normalized === 'true' || normalized === 'false' ? normalized.toUpperCase() : String(value).toUpperCase()
  const colorClass = tone === 'secondary' ? 'bg-light text-gray-700' : `bg-light-${tone} text-${tone}`
  return (
    <span className={`d-inline-flex align-items-center gap-2 rounded-pill px-3 py-1 fs-7 fw-bold ${colorClass}`}>
      <span
        className={`rounded-circle ${tone === 'secondary' ? 'bg-gray-600' : `bg-${tone}`}`}
        style={{width: 7, height: 7, display: 'inline-block'}}
      />
      {label}
    </span>
  )
}

let dashboardErrorTooltipCounter = 0

const DashboardErrorMessageCell: React.FC<{message: string}> = ({message}) => {
  const textRef = React.useRef<HTMLDivElement>(null)
  const [isTruncated, setIsTruncated] = React.useState(false)
  const [tooltipId] = React.useState(() => `dashboard-error-${++dashboardErrorTooltipCounter}`)

  React.useEffect(() => {
    const el = textRef.current
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth)
  })

  return (
    <div className='d-flex align-items-center gap-2' style={{minWidth: 0}}>
      <KTIcon iconName='cross-circle' className='fs-4 text-danger flex-shrink-0' />
      <div
        ref={textRef}
        data-tooltip-id={tooltipId}
        data-tooltip-content={message}
        style={{minWidth: 0, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}
      >
        {message}
      </div>
      {isTruncated && (
        <Tooltip id={tooltipId} style={{maxWidth: '400px', wordWrap: 'break-word', whiteSpace: 'normal'}} />
      )}
    </div>
  )
}

// Red error icon before the message, one line with a hover tooltip showing the full text.
export const renderDashboardErrorWithIcon = (message: string | null | undefined): React.ReactNode => {
  if (!message) return '-'
  return <DashboardErrorMessageCell message={message} />
}

const OUTLINE_AVATAR_ICONS: Record<string, React.ReactNode> = {
  'manage-buyers': (
    <>
      <path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
      <circle cx='9' cy='7' r='4' />
      <path d='M22 21v-2a4 4 0 0 0-3-3.87' />
      <path d='M16 3.13a4 4 0 0 1 0 7.75' />
    </>
  ),
  user: (
    <>
      <path d='M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2' />
      <circle cx='12' cy='7' r='4' />
    </>
  ),
}

// Round icon avatar (e.g. 'user' for admins, 'manage-buyers' for buyers) followed by the full name.
export const renderDashboardUserName = (fullName: string, iconName: string = 'user'): React.ReactNode => {
  const name = fullName.trim()
  if (!name) return '-'
  return (
    <div className='d-flex align-items-center gap-3'>
      <span
        className='d-inline-flex align-items-center justify-content-center rounded-circle bg-light-primary text-primary flex-shrink-0'
        style={{width: 32, height: 32}}
      >
        {OUTLINE_AVATAR_ICONS[iconName] ? (
          // Same thin outline icons as the sidebar's "Manage Buyer" / "Manage Users" items.
          <svg
            width='15'
            height='15'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
          >
            {OUTLINE_AVATAR_ICONS[iconName]}
          </svg>
        ) : (
          <KTIcon iconName={iconName} className='fs-3 text-primary' />
        )}
      </span>
      <span className='fw-semibold'>{name}</span>
    </div>
  )
}

const CONTACT_ICONS: Record<'email' | 'phone', React.ReactNode> = {
  email: (
    <>
      <rect x='2' y='4' width='20' height='16' rx='2' />
      <path d='m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7' />
    </>
  ),
  phone: (
    <path d='M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z' />
  ),
}

// Small outline email / phone icon before the value; empty or placeholder values stay plain text.
export const renderDashboardContact = (
  type: 'email' | 'phone',
  value: string | null | undefined
): React.ReactNode => {
  const text = (value ?? '').toString().trim()
  if (!text || text === '-' || text === 'N/A' || text === 'NaN') return text || '-'
  return (
    <span className='d-inline-flex align-items-center gap-2' style={{minWidth: 0}}>
      <svg
        width='15'
        height='15'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='text-gray-600 flex-shrink-0'
        aria-hidden='true'
      >
        {CONTACT_ICONS[type]}
      </svg>
      <span>{text}</span>
    </span>
  )
}
